import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { queryClient } from '../../../../../../config/queryClient';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { toast } from '../../../../../CommonComponents/OPL/Toast/Toast';
import { whatIfsQueryKeys } from '../../api/queryKeys';
import { useApplyWhatIfMutation } from '../../api/useApplyWhatIfMutation';
import { useDeleteWhatIfMutation } from '../../api/useDeleteWhatIfMutation';
import { WhatIfTypeDTO } from '../../api/useWhatIfGroupsQuery';
import { useWhatIfOptionsQuery } from '../../api/useWhatIfOptionsQuery';
import { WhatIfError } from './WhatIf.error';
import { LoadingWhatIf } from './WhatIf.loading';
import { getWhatIfsTypesMetadata } from './WhatIf.meta';
import { WhatIfTypeKey } from '../../WhatIfs.meta';

interface WhatIfProps extends WhatIfTypeDTO {
  clientId: number;
  fpId: string;
  whatIfAreaId: string;
  goalId?: string;
  isRetrofit?: boolean; // to be removed after the old whatifs are migrated to OPL
  onBack: () => void;
  onSuccess: () => void;
  onDelete: () => void;
}

export const WhatIf = ({
  onBack,
  onSuccess,
  onDelete,
  clientId,
  fpId,
  whatIfAreaId,
  goalId,
  isRetrofit = false,
  ...whatIfType
}: WhatIfProps) => {
  const {
    Component: WhatIfComponent,
    formSchema,
    defaultValues,
    queryFeature,
  } = getWhatIfsTypesMetadata(whatIfType)[whatIfType.WhatIf.WhatIfKey];

  const {
    data: options,
    isLoading: isLoadingOptions,
    isError: isOptionsError,
    error: optionsError,
    refetch: refetchOptions,
  } = useWhatIfOptionsQuery({
    clientId,
    fpId,
    whatIfTypeName: whatIfType.WhatIf.WhatIfTypeName,
    goalId,
  });

  const form = useForm({
    defaultValues: whatIfType.IsApplied ? whatIfType.WhatIf : defaultValues,
    resolver: formSchema ? yupResolver(formSchema) : undefined,
    context: options,
  });

  const { mutateAsync: applyWhatIf } = useApplyWhatIfMutation();

  const onSubmit = form.handleSubmit(async whatIf => {
    try {
      await applyWhatIf({
        clientId,
        fpId,
        goalId,
        whatIf,
      });

      await queryClient.invalidateQueries(
        whatIfsQueryKeys.groups(clientId, fpId, whatIfAreaId, goalId),
      );

      if (queryFeature) {
        await queryClient.invalidateQueries(queryFeature);
      }

      // Refetch Options (to obtain the new account values) for charitable donations
      if (whatIf.WhatIfKey === WhatIfTypeKey.EstatePlanningCharitableDonations) {
        await queryClient.invalidateQueries(whatIfsQueryKeys.optionsAll(clientId));

        toast.success(
          'Donation applied successfully. You can continue to make additional donations',
        );
      }

      if (whatIf.WhatIfKey === WhatIfTypeKey.AnnuityPurchase) {
        toast.success(
          'Annuity purchase applied successfully. You can continue to make additional annuity purchases',
        );
      }

      onSuccess();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  });

  const { mutateAsync, isLoading: isDeletingWhatIf } = useDeleteWhatIfMutation();

  const handleDelete = async (whatIfKey: string) => {
    try {
      await mutateAsync({ clientId, fpId, goalId, whatIfKey });

      await queryClient.invalidateQueries(
        whatIfsQueryKeys.groups(clientId, fpId, whatIfAreaId, goalId),
      );

      if (queryFeature) {
        await queryClient.invalidateQueries(queryFeature);
      }

      onDelete();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  if (isLoadingOptions) {
    return <LoadingWhatIf />;
  }

  if (isOptionsError) {
    return <WhatIfError error={optionsError} onRetry={refetchOptions} />;
  }

  if (!options) {
    return null;
  }

  return (
    <FormProvider {...form}>
      <form css="display: flex; flex-direction: column; flex: 1; height: 100%;" onSubmit={onSubmit}>
        <Box css="flex-grow: 1; height: 0; overflow: auto;">
          <WhatIfComponent {...whatIfType} options={options} fpId={fpId} />
        </Box>
        <Box
          p="16"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderTop="1px solid"
          borderColor="neutral.200"
        >
          <Button variant="secondary" leftIcon="fas fa-chevron-left" onClick={onBack}>
            Back
          </Button>

          {whatIfType.IsApplied && !isRetrofit && (
            <Button
              variant="errorSecondary"
              isLoading={isDeletingWhatIf}
              disabled={isDeletingWhatIf}
              onClick={() => handleDelete(whatIfType.WhatIf.WhatIfKey)}
              ml="auto"
            >
              Delete
            </Button>
          )}

          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            isLoading={form.formState.isSubmitting}
            ml="8"
          >
            Apply to Plan
          </Button>
        </Box>
      </form>
    </FormProvider>
  );
};
